import { Icon } from '@/lib/icons'
import { labelWords, TASK_STATE_WORD } from '@/lib/words'
import { useState, type ReactNode } from 'react'
import { CalendarClock, Play, Square } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import type { TaskResource } from '@/kinds/task/apply'
import type { ProjectResource } from '@/kinds/project/apply'
import { taskActions } from '@/lib/actions'
import { fmtDateShort, fmtMinutes, todayLocal, fmtDateInPhrase } from '@/lib/time'
import { useUi } from '@/state/ui'
import { labelStyle, priorityTone, stateTone, tintText, tokens, type Hue } from '@/lib/colour'
import { usePeople, usePersonLinks, useSettings, useWorkspaceBySlug, useResource } from '@/db/hooks'
import { personFor } from '@/lib/people'
import { hiddenLabel } from '@/lib/follow'
import { isOwnFrom, useAccess } from '@/state/access'
import { PersonChip } from './PersonBits'
import { useScope } from '@/state/scope'
import { ALL } from '@/kinds/workspace/schema'
import { DeferMenu } from './DeferMenu'
import { ProjectPicker } from './ProjectPicker'
import { Button, IconButton, Tag, cx } from './ui'
import { UnsentMark } from './SyncDot'
import { isOpen, type TaskSpec, type TaskStatus } from '@/kinds/task/schema'

const PRIO = ['', '!', '!!', '!!!']
const PRIO_WORDS = ['', 'Priority', 'High priority', 'Top priority']

export const TaskMeta = ({
  task,
  project,
  showScheduled = true,
}: {
  task: TaskResource
  project?: ProjectResource
  showScheduled?: boolean
}) => {
  const blocker = useResource<TaskSpec, TaskStatus>(task.spec.after) as TaskResource | undefined
  const today = todayLocal()
  const settings = useSettings()
  const access = useAccess()
  const scope = useScope((s) => s.ws)
  const wsBySlug = useWorkspaceBySlug()
  const people = usePeople()
  // who it is with, and who sent it when their handle names someone: one chip each
  const who = [...(usePersonLinks().get(task.id) ?? [])]
  // what the owner captured is not "from" anyone
  const found = personFor(people, task.labels.from)
  const sender = found?.spec.me ? undefined : found
  if (sender && !who.some((p) => p.id === sender.id)) who.push(sender)
  const parts = []
  if (scope === ALL && task.labels.ws) {
    const w = wsBySlug.get(task.labels.ws)
    if (w)
      parts.push(
        <Tag
          key="ws"
          style={{ background: tokens(w.spec.colour as Hue).soft, color: tintText(w.spec.colour as Hue) }}
          title={w.spec.name}
        >
          <Icon value={w.spec.icon} size={12} fallback={w.spec.name} />
        </Tag>,
      )
  }
  if (task.spec.priority > 0)
    parts.push(
      <Tag key="p" tone={priorityTone(task.spec.priority)} title={PRIO_WORDS[task.spec.priority]}>
        {PRIO[task.spec.priority]}
      </Tag>,
    )
  if (task.spec.estimate_minutes) parts.push(<Tag key="e">{fmtMinutes(task.spec.estimate_minutes)}</Tag>)
  // its steps, done of total (finished things may be counted), and what it waits for while that is open
  if (task.status.steps_total)
    parts.push(
      <Tag key="steps">
        {task.status.steps_done ?? 0} of {task.status.steps_total} steps
      </Tag>,
    )
  if (blocker && isOpen(blocker.status.state))
    parts.push(
      <Tag key="after" tone="warn" title={`Waits for ${blocker.spec.title}`}>
        after {blocker.spec.title}
      </Tag>,
    )
  if (showScheduled && task.spec.scheduled)
    parts.push(
      <Tag key="s">
        {fmtDateShort(task.spec.scheduled, today)}
        {task.spec.scheduled_time ? ` ${task.spec.scheduled_time}` : ''}
      </Tag>,
    )
  if (task.spec.due)
    parts.push(
      <Tag key="d" tone={task.spec.due < today ? 'warn' : 'muted'}>
        due {fmtDateInPhrase(task.spec.due, today)}
      </Tag>,
    )
  if (project) {
    const t = tokens(project.spec.colour as Hue | undefined)
    parts.push(
      // the hue's readable text on its tint (tintText), never the raw pastel, which is gone on a light page
      <Tag
        key="proj"
        style={
          project.spec.colour
            ? { background: t.soft, color: tintText(project.spec.colour as Hue) }
            : undefined
        }
      >
        {project.spec.colour && !project.spec.icon && (
          <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full" style={{ background: t.stripe }} />
        )}
        <Icon value={project.spec.icon} size={12} className="mr-1" />
        {project.spec.title}
      </Tag>,
    )
  }
  for (const p of who) parts.push(<PersonChip key={`who-${p.id}`} person={p} />)
  for (const [k, v] of Object.entries(task.labels)) {
    // a from: naming someone shows as their chip; a member's own handle is not news to her
    if (hiddenLabel(k) || (k === 'from' && (sender || isOwnFrom(v, access)))) continue
    const st = labelStyle(k, v, settings.label_styles)
    parts.push(
      <Tag key={`l-${k}`} style={st.style}>
        {st.icon ? `${st.icon} ` : ''}
        {labelWords(k, v)}
      </Tag>,
    )
  }
  if (task.status.state === 'waiting')
    parts.push(
      <Tag key="w" tone={stateTone('waiting')}>
        {TASK_STATE_WORD.waiting}
      </Tag>,
    )
  if (task.status.state === 'someday')
    parts.push(
      <Tag key="sd" tone={stateTone('someday')}>
        {TASK_STATE_WORD.someday}
      </Tag>,
    )
  if (!parts.length) return null
  return <div className="flex flex-wrap gap-1 mt-1">{parts}</div>
}

export type RowMode = 'default' | 'triage' | 'review' | 'compact'

/**
 * A task in a list. `focused` marks the keyboard cursor. Actions are visible on
 * hover/focus on desktop and always on touch (there is no hover).
 */
export const TaskRow = ({
  task,
  project,
  focused,
  mode = 'default',
  index,
  expanded,
  child,
  trailing,
}: {
  task: TaskResource
  project?: ProjectResource
  focused?: boolean
  mode?: RowMode
  index?: number
  expanded?: boolean
  /** a step of the task above it: set in from the edge */
  child?: boolean
  /** a page's own action for the row (Someday's "Make it next"), inside it on the right */
  trailing?: ReactNode
}) => {
  const [defer, setDefer] = useState(false)
  const [pickProject, setPickProject] = useState(false)
  const setCursor = useUi((s) => s.setCursor)
  const running = !!task.status.started_at
  const done = task.status.state === 'done'
  return (
    <li
      // a row of a group-list: the list draws the surface and the hairlines
      className={cx(
        'group transition-colors',
        focused && 'row-focus',
        expanded && 'bg-raised',
        child && 'pl-7',
      )}
      data-task-id={task.id}
      onMouseEnter={() => index !== undefined && setCursor(index)}
    >
      <div className="flex items-start gap-2.5 px-3 py-2.5 min-h-12">
        <button
          type="button"
          aria-label={done ? 'Reopen' : 'Complete'}
          onClick={() => void (done ? taskActions.reopen(task) : taskActions.complete(task))}
          className={cx(
            'mt-0.5 h-[22px] w-[22px] shrink-0 rounded-full border-2 flex items-center justify-center text-[11px] transition-colors',
            done
              ? 'bg-emerald-400 border-emerald-400 text-on-accent'
              : 'border-line-strong hover:border-accent hover:bg-accent-soft',
          )}
        >
          {done ? '✓' : ''}
        </button>
        <Link to="/tasks/$id" params={{ id: task.id }} className="flex-1 min-w-0 py-0.5">
          <div
            className={cx(
              'leading-5 break-words',
              done && 'line-through text-faint',
              running && 'text-accent',
            )}
          >
            <Icon value={task.spec.icon} className="mr-1.5" />
            {task.spec.title}
          </div>
          <UnsentMark id={task.id} />
          {mode !== 'compact' && <TaskMeta task={task} project={project} showScheduled={mode !== 'triage'} />}
        </Link>
        {trailing && <div className="shrink-0 self-center">{trailing}</div>}
        {mode === 'default' && !done && (
          <div className="flex items-center gap-0.5 shrink-0">
            <IconButton
              label={running ? 'Stop' : 'Start'}
              onClick={() => void taskActions.toggleTimer(task)}
              className={running ? 'text-accent' : ''}
            >
              {running ? (
                <Square size={14} fill="currentColor" aria-hidden />
              ) : (
                <Play size={15} fill="currentColor" aria-hidden />
              )}
            </IconButton>
            {/* max-sm, not `hidden sm:inline-flex`: IconButton's own inline-flex beats a bare `hidden` */}
            <IconButton label="Reschedule" onClick={() => setDefer(true)} className="max-sm:hidden">
              <CalendarClock size={16} aria-hidden />
            </IconButton>
          </div>
        )}
      </div>
      {mode === 'triage' && (
        <div className="flex flex-wrap gap-1 px-3 pb-2.5">
          <Button variant="chip" onClick={() => void taskActions.move(task, 'next')}>
            Next
          </Button>
          <Button variant="chip" onClick={() => void taskActions.move(task, 'someday')}>
            Someday
          </Button>
          <Button variant="chip" onClick={() => setDefer(true)}>
            Schedule
          </Button>
          <Button variant="chip" onClick={() => setPickProject(true)}>
            Project
          </Button>
          <Button variant="chip" onClick={() => void taskActions.drop(task)}>
            Drop
          </Button>
        </div>
      )}
      {defer && <DeferMenu task={task} open={defer} onClose={() => setDefer(false)} />}
      {pickProject && (
        <ProjectPicker
          task={task}
          open={pickProject}
          onClose={() => setPickProject(false)}
          current={project?.id}
        />
      )}
    </li>
  )
}
